import { clsx } from 'clsx';
import { getFamiliars, getBeast } from '@character-creator/data';
import type { Beast } from '@character-creator/types';

interface FamiliarSelectorProps {
  selectedFamiliarId: string | null;
  onSelect: (familiarId: string | null) => void;
  compact?: boolean;
}

function formatSpeed(speed: Beast['speed']): string {
  const parts: string[] = [];
  if (speed.walk) parts.push(`${speed.walk} ft`);
  if (speed.fly) parts.push(`fly ${speed.fly} ft`);
  if (speed.swim) parts.push(`swim ${speed.swim} ft`);
  if (speed.climb) parts.push(`climb ${speed.climb} ft`);
  if (speed.burrow) parts.push(`burrow ${speed.burrow} ft`);
  return parts.join(', ') || '0 ft';
}

export function FamiliarSelector({
  selectedFamiliarId,
  onSelect,
  compact = false,
}: FamiliarSelectorProps) {
  const familiars = getFamiliars();
  const selectedFamiliar = selectedFamiliarId ? getBeast(selectedFamiliarId) : null;

  // Compact view - shows selected familiar with change option
  if (compact && selectedFamiliar) {
    return (
      <div className="p-3 bg-background-elevated rounded-lg border border-accent-gold/30">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-text-muted">Familiar: </span>
            <span className="text-sm font-medium text-text-primary">{selectedFamiliar.name}</span>
          </div>
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="text-xs text-text-muted hover:text-error transition-colors"
          >
            (change)
          </button>
        </div>
        <div className="mt-1 text-xs text-text-muted flex gap-3">
          <span>AC {selectedFamiliar.armorClass}</span>
          <span>HP {selectedFamiliar.hitPoints.average}</span>
          <span>{formatSpeed(selectedFamiliar.speed)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-background-elevated rounded-lg border border-border-default">
      <h5 className="text-sm font-medium text-text-primary mb-2">
        Choose Your Familiar
        {!selectedFamiliarId && (
          <span className="text-warning ml-2 text-xs">(Required)</span>
        )}
      </h5>
      <p className="text-xs text-text-muted mb-4">
        Select a creature to serve as your familiar. The familiar has the statistics of the chosen form, though it is a celestial, fey, or fiend instead of a beast.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
        {familiars.map((beast) => {
          const isSelected = selectedFamiliarId === beast.id;
          return (
            <button
              key={beast.id}
              type="button"
              onClick={() => onSelect(beast.id)}
              className={clsx(
                'text-left p-3 rounded-lg border transition-all duration-200',
                isSelected
                  ? 'bg-accent-gold/10 border-accent-gold'
                  : 'bg-background-secondary border-background-elevated hover:border-text-muted'
              )}
            >
              <div className="font-medium text-text-primary text-sm">{beast.name}</div>
              <div className="text-xs text-text-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                <span>AC {beast.armorClass}</span>
                <span>HP {beast.hitPoints.average}</span>
              </div>
              <div className="text-xs text-text-muted">
                {formatSpeed(beast.speed)}
              </div>
              {beast.senses && beast.senses.length > 0 && (
                <div className="text-xs text-text-secondary mt-1">
                  {beast.senses.join(', ')}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
